/**
 * Microsoft Advertising Error Parser (Story 19.1, Task 2)
 *
 * Maps Microsoft Advertising API error responses to standardized error types.
 *
 * Microsoft surfaces errors in TWO shapes:
 *
 * 1. REST error envelope (most endpoints, modern v13 REST):
 *    { "error": { "code": "...", "message": "..." } }
 *
 * 2. SOAP-style fault codes returned via `OperationErrors[]` / `BatchErrors[]`
 *    arrays even on the REST surface — these come from the Customer Management
 *    Service v13 SOAP fault catalogue. Example codes we explicitly handle:
 *      - 105   InvalidCredentials
 *      - 106   UserIsNotAuthorized
 *      - 117   AuthenticationTokenExpired
 *      - 118   InvalidDeveloperToken
 *      - 4204  CallRateExceeded
 *
 * The parser inspects HTTP status first (the cheapest signal) and only falls
 * back to body-shape sniffing when the status isn't in our table.
 *
 * @see https://learn.microsoft.com/en-us/advertising/customer-management-service/customermanagementservice-fault-codes
 */

// ============================================================================
// Types
// ============================================================================

export type MicrosoftAdsErrorType =
  | "token_expired"
  | "permission_denied"
  | "not_found"
  | "rate_limited"
  | "provider_unavailable"
  | "invalid_developer_token"
  | "unknown";

export interface MicrosoftAdsApiError {
  type: MicrosoftAdsErrorType;
  message: string;
  status: number | null;
  retryable: boolean;
}

interface RestErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
  };
  OperationErrors?: Array<{
    Code?: number | string;
    ErrorCode?: string;
    Message?: string;
  }>;
  BatchErrors?: Array<{
    Code?: number | string;
    ErrorCode?: string;
    Message?: string;
  }>;
  Message?: string;
}

// ============================================================================
// Status Code Mapping
// ============================================================================

const STATUS_CODE_MAP: Record<
  number,
  { type: MicrosoftAdsErrorType; retryable: boolean }
> = {
  401: { type: "token_expired", retryable: false },
  403: { type: "permission_denied", retryable: false },
  404: { type: "not_found", retryable: false },
  429: { type: "rate_limited", retryable: true },
  500: { type: "provider_unavailable", retryable: true },
  502: { type: "provider_unavailable", retryable: true },
  503: { type: "provider_unavailable", retryable: true },
  504: { type: "provider_unavailable", retryable: true },
};

// ============================================================================
// SOAP Fault Code Mapping
// ============================================================================
//
// Codes verified against Microsoft's Customer Management Service fault-code
// reference. Numeric codes are the canonical identifier; ErrorCode strings are
// supplementary and may differ across endpoints.

const SOAP_FAULT_CODE_MAP: Record<
  string,
  { type: MicrosoftAdsErrorType; retryable: boolean }
> = {
  // Authentication / authorisation
  "105": { type: "token_expired", retryable: false },
  "106": { type: "permission_denied", retryable: false },
  "117": { type: "token_expired", retryable: false },
  "118": { type: "invalid_developer_token", retryable: false },
  // Rate limit
  "4204": { type: "rate_limited", retryable: true },
};

const SOAP_FAULT_NAME_MAP: Record<
  string,
  { type: MicrosoftAdsErrorType; retryable: boolean }
> = {
  AuthenticationTokenExpired: { type: "token_expired", retryable: false },
  InvalidCredentials: { type: "token_expired", retryable: false },
  UserIsNotAuthorized: { type: "permission_denied", retryable: false },
  InvalidDeveloperToken: { type: "invalid_developer_token", retryable: false },
  CallRateExceeded: { type: "rate_limited", retryable: true },
};

// ============================================================================
// Error Parser
// ============================================================================

/**
 * Parses a Microsoft Ads error response into a standardized error object.
 *
 * Resolution order:
 *   1. HTTP status (fastest, most reliable)
 *   2. Numeric SOAP fault code (`OperationErrors[].Code` or `BatchErrors[].Code`)
 *   3. Symbolic SOAP fault code (`OperationErrors[].ErrorCode`)
 *   4. REST envelope `error.message`
 *   5. Fallback to "unknown"
 *
 * @returns The mapped error, or null when the input is empty/successful (HTTP 2xx
 *          with no error body).
 */
export function parseMicrosoftAdsApiError(
  responseBody: unknown,
  httpStatus?: number
): MicrosoftAdsApiError | null {
  const body = (responseBody ?? {}) as RestErrorEnvelope;

  // ---- 1. HTTP status mapping ------------------------------------------------
  if (httpStatus && (httpStatus < 200 || httpStatus >= 300)) {
    const mapping = STATUS_CODE_MAP[httpStatus];
    if (mapping) {
      const message = extractErrorMessage(body, httpStatus);
      return {
        type: mapping.type,
        message,
        status: httpStatus,
        retryable: mapping.retryable,
      };
    }
  }

  // ---- 2/3. SOAP fault codes (numeric + symbolic) ----------------------------
  const errorEntries = [
    ...(body.OperationErrors ?? []),
    ...(body.BatchErrors ?? []),
  ];
  for (const entry of errorEntries) {
    if (entry.Code != null) {
      const codeStr = String(entry.Code);
      const mapping = SOAP_FAULT_CODE_MAP[codeStr];
      if (mapping) {
        return {
          type: mapping.type,
          message:
            entry.Message ??
            `Microsoft Ads error ${codeStr}` +
              (entry.ErrorCode ? ` (${entry.ErrorCode})` : ""),
          status: httpStatus ?? null,
          retryable: mapping.retryable,
        };
      }
    }
    if (entry.ErrorCode) {
      const mapping = SOAP_FAULT_NAME_MAP[entry.ErrorCode];
      if (mapping) {
        return {
          type: mapping.type,
          message:
            entry.Message ??
            `Microsoft Ads error: ${entry.ErrorCode}`,
          status: httpStatus ?? null,
          retryable: mapping.retryable,
        };
      }
    }
  }

  // ---- 4. REST envelope without explicit code mapping ------------------------
  if (body.error?.message) {
    return {
      type: "unknown",
      message: body.error.message,
      status: httpStatus ?? null,
      retryable: false,
    };
  }

  // ---- 5. No identifiable error -----------------------------------------------
  if (!httpStatus || (httpStatus >= 200 && httpStatus < 300)) {
    return null;
  }

  return {
    type: "unknown",
    message: extractErrorMessage(body, httpStatus),
    status: httpStatus,
    retryable: false,
  };
}

// ============================================================================
// Typed error class
// ============================================================================

/**
 * Thrown by `fetchMicrosoftAdsUserInfo` / `fetchMicrosoftAdsAccountsSearch`
 * when Microsoft returns a non-2xx response. Carries the parsed error type so
 * the adapter can route it to the correct `AdapterErrorCode` without
 * string-matching the message (review #2 M3).
 */
export class MicrosoftAdsApiCallError extends Error {
  readonly type: MicrosoftAdsErrorType;
  readonly status: number | null;
  readonly retryable: boolean;
  constructor(parsed: MicrosoftAdsApiError, prefix: string) {
    super(`${prefix}: ${parsed.message}`);
    this.name = "MicrosoftAdsApiCallError";
    this.type = parsed.type;
    this.status = parsed.status;
    this.retryable = parsed.retryable;
  }
}

/**
 * Maps a Microsoft Ads error type to the corresponding `AdapterErrorCode`.
 * Used by the adapter so we don't string-match localised provider messages.
 */
export function microsoftAdsErrorTypeToAdapterCode(
  type: MicrosoftAdsErrorType
): "token_expired" | "permission_denied" | "rate_limited" | "provider_unavailable" | "invalid_response" | "account_not_found" {
  switch (type) {
    case "token_expired":
      return "token_expired";
    case "permission_denied":
      return "permission_denied";
    case "not_found":
      // Microsoft 404 can mean "no accounts under this customer" (User/Query
      // → benign onboarding state) OR "endpoint URL changed" (post-deploy
      // schema drift). We prefer `invalid_response` over `account_not_found`
      // here because callers in 19.1 already explicitly check
      // `customerRoles.length === 0` for the benign case before throwing —
      // any 404 surfacing here is more likely a deploy-time issue. (review #3 M2)
      return "invalid_response";
    case "rate_limited":
      return "rate_limited";
    case "provider_unavailable":
      return "provider_unavailable";
    case "invalid_developer_token":
      // Developer token misconfig is a deployment issue; surface as
      // permission_denied so the token health monitor doesn't queue
      // pointless retries.
      return "permission_denied";
    case "unknown":
    default:
      return "invalid_response";
  }
}

/**
 * Pulls the most descriptive message we can find out of a Microsoft Ads error
 * envelope, with a sensible fallback when nothing structured is present.
 */
function extractErrorMessage(body: RestErrorEnvelope, status: number): string {
  if (body.error?.message) return body.error.message;

  const firstOpError = body.OperationErrors?.[0];
  if (firstOpError?.Message) return firstOpError.Message;
  const firstBatchError = body.BatchErrors?.[0];
  if (firstBatchError?.Message) return firstBatchError.Message;

  if (body.Message) return body.Message;

  return `Microsoft Ads API error: HTTP ${status}`;
}
